import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ROUTES, API_DOMAIN } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';
import * as listCache from '../../../../services/listCache';
import { fmtDate } from '../../../../utils/fmt';

// ── Auth ─────────────────────────────────────────────────────────────────────
const isSuperAdmin = () => {
  try {
    if (localStorage.getItem('is_superadmin') === 'true') return true;
    return !!JSON.parse(localStorage.getItem('user') || '{}').is_superadmin;
  } catch { return false; }
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function buildHeaders() {
  const token = localStorage.getItem('customer_management_token');
  const tenantId = localStorage.getItem('crm_tenant_id');
  const tenantToken = localStorage.getItem('crm_tenant_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    ...(tenantToken ? { 'X-Tenant-Token': tenantToken } : {}),
  };
}

function pageRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current, current - 1, current + 1].filter(p => p >= 1 && p <= total));
  return [...set].sort((a, b) => a - b);
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_COLORS = {
  'Prospect': '#f59e0b', 'Active': '#22c55e', 'Inactive': '#94a3b8',
  'At Risk': '#ef4444', 'Former': '#64748b', 'VIP': '#8b5cf6',
};

const TYPE_ICONS = {
  'Individual': '👤', 'Business': '🏢', 'Enterprise': '🏛', 'Partner': '🤝',
};

const TIER_COLORS = {
  'Platinum': '#6d28d9', 'Gold': '#92400e', 'Silver': '#475569',
  'Bronze': '#9a3412', 'Standard': '#166534',
};


const Avatar = ({ name }) => {
  const initials = name
    ? name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '?';
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: 'var(--color-primary-pale)', color: 'var(--color-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700,
    }}>{initials}</div>
  );
};

const StatusBadge = ({ value }) => {
  const color = STATUS_COLORS[value] || '#94a3b8';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 10px', borderRadius: 99, whiteSpace: 'nowrap',
      background: color + '18', border: `1px solid ${color}40`,
      fontSize: 11, fontWeight: 600, color,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {value || '—'}
    </span>
  );
};

const SKEL = Array.from({ length: 6 });

// ── Component ─────────────────────────────────────────────────────────────────
const CustomerListDetails = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [fetchVersion, setFetchVersion] = useState(0);
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const refetch = useCallback(() => setFetchVersion(v => v + 1), []);

  useEffect(() => {
    let cancelled = false;

    const url = `${API_DOMAIN}${API_ROUTES.customer}?page=${page}&page_size=${pageSize}`;
    const applyData = (data) => {
      const items = Array.isArray(data) ? data : (data.results || []);
      setCustomers(items);
      setTotalItems(Array.isArray(data) ? items.length : (data.count ?? items.length));
    };

    // Stale-while-revalidate: instant render from cache, background refresh only if stale.
    const cached = listCache.get(url);
    if (cached) {
      applyData(cached.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (!cached || cached.isStale) {
      listCache
        .fetchDeduped(url, buildHeaders())
        .then(data => {
          if (cancelled) return;
          listCache.set(url, data);
          applyData(data);
        })
        .catch(() => {
          if (cancelled) return;
          if (!cached) toast?.addToast({ type: 'error', message: 'Failed to load customers.' });
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }

    return () => { cancelled = true; };
  }, [page, pageSize, fetchVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API_DOMAIN}${API_ROUTES.customer}${id}/`, {
        method: 'DELETE',
        headers: buildHeaders(),
      });
      if (!r.ok) throw new Error();
      toast?.addToast({ type: 'success', message: 'Customer deleted.' });
      listCache.invalidate((u) => u.startsWith(`${API_DOMAIN}${API_ROUTES.customer}`));
      refetch();
    } catch {
      toast?.addToast({ type: 'error', message: 'Failed to delete customer.' });
    }
  };

  // Client-side search filters within current page
  const q = search.toLowerCase();
  const filtered = q
    ? customers.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.industry || '').toLowerCase().includes(q) ||
        (c.status || '').toLowerCase().includes(q)
      )
    : customers;

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pages = pageRange(page, totalPages);
  const showFrom = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const showTo = Math.min(page * pageSize, totalItems);
  const goTo = (p) => setPage(Math.max(1, Math.min(p, totalPages)));
  const changePageSize = (n) => { setPageSize(n); setPage(1); };

  return (
    <div>
      <div className="card">
        {/* ── Search bar ── */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>🔍</span>
            <input
              className="form-input"
              style={{ paddingLeft: 32 }}
              placeholder="Search by name, company, email, status…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {loading
              ? 'Loading…'
              : q
                ? `${filtered.length.toLocaleString()} match${filtered.length !== 1 ? 'es' : ''} on this page`
                : `${totalItems.toLocaleString()} customer${totalItems !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* ── Table ── */}
        <div className="table-scroll">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Status</th>
                <th>Type</th>
                <th>Company</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Since</th>
                <th style={{ textAlign: 'right', width: 1 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? SKEL.map((_, i) => (
                    <tr key={i}>
                      {[55, 50, 40, 55, 65, 45, 45, 80].map((w, j) => (
                        <td key={j}><div className="skeleton" style={{ height: 13, width: `${w}%`, borderRadius: 4 }} /></td>
                      ))}
                    </tr>
                  ))
                : filtered.length === 0
                  ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="empty-state">
                          <div className="empty-state-icon-wrap">
                            {search ? (
                              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
                              </svg>
                            ) : (
                              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 8v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
                                <path d="M3 8l2.5-4.5A1 1 0 0 1 6.37 3h11.26a1 1 0 0 1 .87.5L21 8" />
                                <path d="M3 8h18" /><path d="M9 12h6" />
                              </svg>
                            )}
                          </div>
                          <p className="empty-state-title">{search ? 'No matching records' : 'No customers yet'}</p>
                          <p className="empty-state-text">
                            {search ? `We couldn't find anything for "${search}" on this page.` : 'Click "New Customer" to add your first one.'}
                          </p>
                          {search && (
                            <button className="btn btn-secondary btn-sm" style={{ marginTop: 16 }} onClick={() => setSearch('')}>
                              Clear search
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                  : filtered.map(customer => (
                    <tr key={customer.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={customer.name} />
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: 13 }}>
                              {customer.name || `Customer #${customer.id}`}
                            </div>
                            {customer.tier && (
                              <div style={{ fontSize: 10, color: TIER_COLORS[customer.tier] || 'var(--text-muted)', fontWeight: 600, marginTop: 1 }}>
                                {customer.tier}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td><StatusBadge value={customer.status} /></td>
                      <td style={{ fontSize: 12, color: 'var(--text-sub)' }}>
                        {TYPE_ICONS[customer.customer_type] || ''} {customer.customer_type || '—'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-sub)' }}>{customer.company || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-sub)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {customer.email || '—'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-sub)', whiteSpace: 'nowrap' }}>{customer.phone || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(customer.customer_since)}</td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/customer/details/${btoa(customer.id)}`)}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/></svg>
                            View
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/customer/stage/input/${btoa(customer.id)}`)}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"/></svg>
                            Edit
                          </button>
                          {isSuperAdmin() && (
                            <button className="btn btn-danger btn-sm" onClick={() => setPendingDelete(customer.id)}>
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M2 4h12M6 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4"/></svg>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && totalItems > 0 && (
          <div className="pagination-wrap">
            <span className="pagination-info">
              {showFrom.toLocaleString()}–{showTo.toLocaleString()} of {totalItems.toLocaleString()}
            </span>

            <div className="pagination-buttons">
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(1)} disabled={page === 1} title="First">«</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page - 1)} disabled={page === 1} title="Previous">‹</button>
              {pages.map((p, idx) => {
                const prev = pages[idx - 1];
                return (
                  <React.Fragment key={p}>
                    {prev && p - prev > 1 && <span className="pagination-ellipsis">…</span>}
                    <button
                      className={`btn btn-sm pagination-page-btn${p === page ? ' active' : ''}`}
                      onClick={() => goTo(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page + 1)} disabled={page === totalPages} title="Next">›</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(totalPages)} disabled={page === totalPages} title="Last">»</button>
            </div>

            <div className="pagination-size-wrap">
              <label className="pagination-size-label">Rows:</label>
              <select
                className="pagination-size-select"
                value={pageSize}
                onChange={e => changePageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {isSuperAdmin() && (
        <ConfirmModal
          open={pendingDelete !== null}
          title="Delete Customer"
          message="Are you sure you want to delete this customer? All associated data may also be removed."
          confirmLabel="Delete"
          confirmDanger
          onConfirm={() => { handleDelete(pendingDelete); setPendingDelete(null); }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default CustomerListDetails;
